/*
//EXAMPLE USE
const columns = React.useMemo(
    () => [
      {
        Header: 'Age',
        accessor: 'age',
      },
      {
        Header: 'Visits',
        accessor: 'visits',
      },
      {
        Header: 'Status',
        accessor: 'status',
      },
      {
        Header: 'Profile Progress',
        accessor: 'progress',
      },
    ],
    []
  );
  const data = [
    { age: 1, visits: 2, status: 'example', progress: '45%' },
    { age: 1, visits: 2, status: 'example', progress: '45%' },
    { age: 1, visits: 2, status: 'example', progress: '45%' },
    { age: 1, visits: 2, status: 'example', progress: '45%' },
    { age: 1, visits: 2, status: 'example', progress: '45%' },
    { age: 1, visits: 2, status: 'example', progress: '45%' },
  ];

  <Table
    columns={columns}
    data={data}
    styleOptions={{
      backgroundColor: theme.white,
      primaryColor: theme.white,
      secondaryColor: theme.light2,
      textColor: theme.dark,
      fullWidth: true,
    }}
  />
*/

/* eslint-disable no-nested-ternary */
import React from 'react';
import { useTable, useSortBy } from 'react-table';
import PropTypes from 'prop-types';
import { v4 } from 'uuid';
import { useSelector } from 'react-redux';
import * as S from './styles';

function Table({
  columns,
  data,
  styleOptions,
  className,
  onRowClick,
  onMouseOut,
  header,
  selectedRow,
  hasFooter,
  limit,
  responsiveHeader,
  isFetching,
  placeholderNumElements,
  placeholderNumColumns,
  disableSort,
  fixedColumn,
  customTr,
}) {
  const { data: match } = useSelector(state => state.matches.selectedMatch);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    footerGroups,
  } = useTable(
    {
      columns,
      data: data || [],
    },
    useSortBy
  );

  const PlaceholderHeader = () => {
    const placeholderColumns =
      columns && columns.length > 0
        ? columns
        : [...Array(placeholderNumColumns)];
    const row = placeholderColumns.map(() => {
      return (
        <th key={v4()}>
          <S.PlaceholderData />
        </th>
      );
    });
    return <tr key={v4()}>{row}</tr>;
  };

  const PlaceholderData = () => {
    const placeholderColumns =
      columns && columns.length > 0
        ? columns
        : [...Array(placeholderNumColumns)];
    const row = placeholderColumns.map(() => {
      return (
        <td key={v4()}>
          <S.PlaceholderData />
        </td>
      );
    });
    return (
      <>
        {[...Array(placeholderNumElements)].map(() => (
          <tr key={v4()}>{row}</tr>
        ))}
      </>
    );
  };

  const firstPageRows = limit ? rows.slice(0, limit) : rows;
  if (isFetching) {
    return (
      <S.Container
        {...styleOptions}
        className={className}
        onMouseOut={onMouseOut}
        responsiveHeader={responsiveHeader}
        fixedColumn={fixedColumn}
      >
        {header}
        <table>
          <thead>
            <PlaceholderHeader />
          </thead>
          <tbody>
            <PlaceholderData />
          </tbody>
        </table>
      </S.Container>
    );
  }

  const Tr = ({ ...props }) => {
    if (customTr) {
      return customTr(props);
    }
    return <tr {...props} />;
  };

  const Td = ({ ...props }) => {
    return <td {...props} />;
  };

  return (
    <S.Container
      {...styleOptions}
      className={className}
      onMouseOut={onMouseOut}
      responsiveHeader={responsiveHeader}
      fixedColumn={fixedColumn}
    >
      {header}
      <table {...getTableProps()}>
        <thead>
          {isFetching && <PlaceholderHeader />}
          {!isFetching &&
            headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <th
                    {...(disableSort
                      ? column.getHeaderProps()
                      : column.getHeaderProps(column.getSortByToggleProps()))}
                    title={columns[index] ? columns[index].tooltip : null}
                  >
                    <div>
                      {column.render('Header')}
                      <>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <S.IconTable
                              name="chevronDown"
                              size={15}
                              color="dark"
                            />
                          ) : (
                            <S.IconTable
                              name="chevronUp"
                              size={15}
                              color="dark"
                            />
                          )
                        ) : (
                          ''
                        )}
                      </>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {isFetching && <PlaceholderData />}
          {!isFetching &&
            firstPageRows.map(row => {
              prepareRow(row);
              return (
                <Tr
                  item={row.index}
                  {...row.getRowProps({
                    onClick: () => onRowClick(row),
                    className:
                      selectedRow === row.original.id
                        ? 'table--selected'
                        : null,
                  })}
                  className={
                    selectedRow === row.original.id ? 'table--selected' : null
                  }
                  competition={match?.competition}
                >
                  {row.cells.map(cell => {
                    return (
                      <Td {...cell.getCellProps()}>
                        <div>
                          {cell.value !== null && cell.value !== undefined
                            ? cell.value
                            : '-'}
                        </div>
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
        </tbody>
        <tfoot>
          {hasFooter &&
            footerGroups &&
            footerGroups.map(group => (
              <tr {...group.getFooterGroupProps()}>
                {group.headers.map(column => (
                  <td {...column.getFooterProps()}>
                    {column.render('Footer')}
                  </td>
                ))}
              </tr>
            ))}
        </tfoot>
      </table>
    </S.Container>
  );
}

Table.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  styleOptions: PropTypes.object,
  className: PropTypes.string,
  onRowClick: PropTypes.func,
  onMouseOut: PropTypes.func,
  header: PropTypes.node,
  selectedRow: PropTypes.number,
  hasFooter: PropTypes.bool,
  limit: PropTypes.number,
  responsiveHeader: PropTypes.bool,
  isFetching: PropTypes.bool,
  placeholderNumElements: PropTypes.number,
  placeholderNumColumns: PropTypes.number,
  disableSort: PropTypes.bool,
  fixedColumn: PropTypes.bool,
  customTr: PropTypes.func,
};

Table.defaultProps = {
  styleOptions: {},
  className: null,
  onRowClick: () => {},
  onMouseOut: () => {},
  header: null,
  selectedRow: null,
  hasFooter: false,
  limit: null,
  responsiveHeader: false,
  fixedColumn: false,
  isFetching: false,
  placeholderNumElements: 5,
  placeholderNumColumns: 5,
  disableSort: false,
  customTr: null,
};

export default Table;
